import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import { trpc } from "../../app/trpc.js";
import { Button, buttonClass, Card, ChevronLeftIcon, Input, Label, Notice, Spinner } from "../../ui/index.js";
import { AdminTopBar } from "./AdminTopBar.js";
import { alertLabel, auditLabel, fmtDate, fmtInt, fmtUsd, salonStatusLabel, subscriptionLabel } from "./format.js";

const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** Сохранить файл выгрузки через временную ссылку. */
function download(filename: string, data: BlobPart, type: string) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Салон в админке: владелец и его приглашение, Instagram, подписка, расход, тревоги, журнал; настройки оплаты, выгрузка, удаление. */
export function AdminSalonPage() {
  const { salonId = "" } = useParams();
  const location = useLocation();
  const q = useQuery(trpc.billing.admin.salon.queryOptions({ salonId }));
  const ownerInvite = useQuery(trpc.auth.admin.ownerInvite.queryOptions({ salonId }));
  const refetch = () => {
    void q.refetch();
    void ownerInvite.refetch();
  };
  const update = useMutation(trpc.billing.admin.updateBilling.mutationOptions({ onSuccess: refetch }));
  // У каждой выгрузки своё состояние: нажатие на одну кнопку не должно выглядеть как нажатие на обе.
  const csvDone = (f: { filename: string; csv: string }) => download(f.filename, f.csv, "text/csv;charset=utf-8");
  // XLSX рядом с CSV: Google Таблицы на Android читают CSV с BOM как Windows-1252 (решение 04.11.2026).
  const xlsxDone = (f: { filename: string; base64: string }) =>
    download(
      f.filename,
      Uint8Array.from(atob(f.base64), (c) => c.charCodeAt(0)),
      XLSX_TYPE,
    );
  const exportClients = useMutation(trpc.billing.admin.exportCsv.mutationOptions({ onSuccess: csvDone }));
  const exportBookings = useMutation(trpc.billing.admin.exportCsv.mutationOptions({ onSuccess: csvDone }));
  const xlsxClients = useMutation(trpc.billing.admin.exportXlsx.mutationOptions({ onSuccess: xlsxDone }));
  const xlsxBookings = useMutation(trpc.billing.admin.exportXlsx.mutationOptions({ onSuccess: xlsxDone }));
  const exportError = [exportClients, exportBookings, xlsxClients, xlsxBookings].find((m) => m.isError)?.error;
  const del = useMutation(trpc.billing.admin.deleteSalon.mutationOptions({ onSuccess: refetch }));
  const restore = useMutation(trpc.billing.admin.restoreSalon.mutationOptions({ onSuccess: refetch }));
  const resolve = useMutation(trpc.billing.admin.resolveAlert.mutationOptions({ onSuccess: refetch }));

  const [cap, setCap] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const settings = q.data?.settings;
  useEffect(() => {
    if (settings) setCap(String(settings.dailyCapUsd));
  }, [settings]);

  // Строка с «Все салоны» и «Выйти» есть и пока салон грузится, и если не нашёлся.
  const shell = (children: ReactNode) => (
    <div className="mx-auto max-w-4xl space-y-4 px-4 py-6">
      <AdminTopBar
        left={
          <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink">
            <ChevronLeftIcon className="-ml-1 h-4 w-4 shrink-0" />
            Все салоны
          </Link>
        }
      />
      {children}
    </div>
  );
  if (q.isPending) return shell(<Spinner />);
  if (q.isError) return shell(<Notice kind="error">Салон не найден</Notice>);
  const s = q.data;
  const deleted = s.status === "deleted";
  const pending = ownerInvite.data ?? null;
  const ownerLine = s.ownerEmail
    ? `владелец ${s.ownerEmail}`
    : pending
      ? `владелец приглашён: ${pending.email}, до ${fmtDate(pending.expiresAt)}`
      : "владельца нет";
  const mailFailed = (location.state as { mailFailed?: boolean } | null)?.mailFailed === true;

  const onCap = (e: FormEvent) => {
    e.preventDefault();
    const v = Number(cap.replace(",", "."));
    if (Number.isFinite(v)) update.mutate({ salonId, dailyCapUsd: v });
  };
  const onDelete = (e: FormEvent) => {
    e.preventDefault();
    del.mutate({ salonId, confirmName });
  };

  return shell(
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{s.name}</h1>
          <div className={`text-sm ${deleted ? "text-red-600" : "text-ink-3"}`}>
            {salonStatusLabel[s.status] ?? s.status} · {ownerLine} · создан {fmtDate(s.createdAt)}
          </div>
        </div>
        {!deleted && (
          <Link to={`/admin/s/${s.id}`} className={buttonClass("primary")}>
            Войти в салон
          </Link>
        )}
      </div>

      {!s.ownerEmail && !deleted && <OwnerInviteCard salonId={salonId} pending={pending} mailFailed={mailFailed} onDone={refetch} />}

      <Card>
        <h2 className="mb-2 font-semibold">Instagram</h2>
        {s.channels.length === 0 ? (
          <div className="text-sm text-ink-3">Не подключён</div>
        ) : (
          s.channels.map((c) => (
            <div key={`${c.channel}-${c.username}`} className="text-sm">
              <span className={c.status === "connected" ? "text-green-700" : "text-red-600"}>
                {c.channel} {c.username ? `@${c.username}` : ""} · {c.status}
              </span>
              <span className="text-ink-3">
                {" "}
                · вебхук {fmtDate(c.lastWebhookAt)} · токен до {fmtDate(c.tokenExpiresAt)}
              </span>
              {c.lastError && <div className="text-red-600">{c.lastError}</div>}
            </div>
          ))
        )}
      </Card>

      <Card>
        <h2 className="mb-2 font-semibold">Подписка</h2>
        <div className="text-sm">
          {subscriptionLabel[s.subscription.status] ?? s.subscription.status}
          {s.subscription.trialEndsAt && ` · пробный до ${fmtDate(s.subscription.trialEndsAt)}`}
          {s.subscription.currentPeriodEnd && ` · период до ${fmtDate(s.subscription.currentPeriodEnd)}`}
          {s.subscription.cancelAt && ` · отмена ${fmtDate(s.subscription.cancelAt)}`}
          {` · подключение €30: ${s.subscription.setupFeePaidAt ? `оплачено ${fmtDate(s.subscription.setupFeePaidAt)}` : "нет"}`}
        </div>
        <div className="mt-3 space-y-2 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={s.settings.setupFee} onChange={(e) => update.mutate({ salonId, setupFee: e.target.checked })} />
            Включить «подключим за вас» (€30) в оплату
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={s.settings.accessOverride === "active"}
              onChange={(e) => update.mutate({ salonId, accessOverride: e.target.checked ? "active" : null })}
            />
            Ручной доступ без подписки (тестовый салон, App Review)
          </label>
        </div>
      </Card>

      <Card>
        <h2 className="mb-2 font-semibold">Расход на модель</h2>
        <div className="mb-3 text-sm">
          сегодня {fmtUsd(s.usage.todayUsd)} · 30 дней {fmtUsd(s.usage.last30Usd)} · {fmtInt(s.usage.last30Calls)} вызовов · {fmtInt(s.usage.last30Tokens)}{" "}
          токенов
        </div>
        <form onSubmit={onCap} className="mb-3 flex items-end gap-2">
          <div>
            <Label htmlFor="cap">Аварийный потолок в сутки, USD</Label>
            <Input id="cap" inputMode="decimal" value={cap} onChange={(e) => setCap(e.target.value)} className="w-32" />
          </div>
          <Button type="submit" variant="secondary" disabled={update.isPending}>
            Сохранить
          </Button>
        </form>
        {update.isError && <Notice kind="error">Не сохранилось</Notice>}
        {s.usageByDay.length > 0 && (
          <table className="text-sm">
            <tbody>
              {s.usageByDay.map((d) => (
                <tr key={d.day}>
                  <td className="pr-4 text-ink-3">{d.day}</td>
                  <td className="pr-4">{fmtUsd(d.costUsd)}</td>
                  <td className="pr-4 text-ink-3">{fmtInt(d.calls)}</td>
                  <td className="text-red-600">{d.capReached ? "потолок" : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <h2 className="mb-2 font-semibold">Тревоги</h2>
        {s.alerts.length === 0 ? (
          <div className="text-sm text-ink-3">Нет</div>
        ) : (
          <ul className="space-y-2 text-sm">
            {s.alerts.map((a) => (
              <li key={a.id} className={a.resolvedAt ? "text-ink-3" : ""}>
                {fmtDate(a.createdAt)} · {alertLabel[a.kind] ?? a.kind} · {a.message}
                {!a.resolvedAt && (
                  <button type="button" className="ml-2 underline" onClick={() => resolve.mutate({ alertId: a.id })}>
                    закрыть
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h2 className="mb-2 font-semibold">Журнал</h2>
        {s.audit.length === 0 ? (
          <div className="text-sm text-ink-3">Пусто</div>
        ) : (
          <ul className="space-y-1 text-sm">
            {s.audit.map((a) => (
              <li key={a.id}>
                {fmtDate(a.createdAt)} · {a.actorEmail ?? "система"} · {auditLabel[a.action] ?? a.action}
                {a.ip ? <span className="text-ink-3"> · {a.ip}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h2 className="mb-2 font-semibold">Отключение</h2>
        <div className="mb-4 flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => exportClients.mutate({ salonId, kind: "clients" })} disabled={exportClients.isPending}>
            {exportClients.isPending ? "Готовим…" : "Клиенты (CSV)"}
          </Button>
          <Button variant="secondary" onClick={() => exportBookings.mutate({ salonId, kind: "bookings" })} disabled={exportBookings.isPending}>
            {exportBookings.isPending ? "Готовим…" : "Записи (CSV)"}
          </Button>
          <Button variant="secondary" onClick={() => xlsxClients.mutate({ salonId, kind: "clients" })} disabled={xlsxClients.isPending}>
            {xlsxClients.isPending ? "Готовим…" : "Клиенты (XLSX)"}
          </Button>
          <Button variant="secondary" onClick={() => xlsxBookings.mutate({ salonId, kind: "bookings" })} disabled={xlsxBookings.isPending}>
            {xlsxBookings.isPending ? "Готовим…" : "Записи (XLSX)"}
          </Button>
        </div>
        <p className="-mt-2 mb-4 text-sm text-ink-3">На телефоне открывайте XLSX: CSV Google Таблицы на Android показывают с битой кириллицей.</p>
        {exportError && (
          <div className="mb-4">
            <Notice kind="error">Не удалось выгрузить: {exportError.message}</Notice>
          </div>
        )}
        {deleted ? (
          <div className="space-y-2 text-sm">
            {del.data?.paddle === "failed" && (
              <Notice kind="error">
                Подписку в Paddle отменить не удалось, отмените её вручную в Paddle. Пока подписка живая, данные салона не сотрутся (тревога в админке).
              </Notice>
            )}
            {del.data?.paddle === "canceled" && <Notice kind="success">Подписка в Paddle отменена.</Notice>}
            <Notice kind="error">
              Салон помечен на удаление. Данные сотрутся {fmtDate(s.purgeAfter)}. Подписка в Paddle отменяется при удалении безвозвратно: после отмены удаления
              салон останется без доступа, пока не оформит подписку заново (без пробного месяца) или вы не включите ручной доступ.
            </Notice>
            <Button variant="secondary" onClick={() => restore.mutate({ salonId })} disabled={restore.isPending}>
              Отменить удаление
            </Button>
          </div>
        ) : (
          <form onSubmit={onDelete} className="space-y-2">
            <p className="text-sm text-ink-2">
              Бот сразу перестанет отвечать, подписка в Paddle отменится сразу, данные сотрутся через 30 дней. Введите название салона для подтверждения.
            </p>
            <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={s.name} />
            {del.isError && <Notice kind="error">Название не совпадает или салон уже удаляется</Notice>}
            {restore.isSuccess && (
              <Notice>Удаление отменено. Подписка в Paddle была отменена при удалении, салону нужно оформить её заново или включить ручной доступ.</Notice>
            )}
            <Button type="submit" variant="dangerGhost" disabled={del.isPending || !confirmName.trim()}>
              Удалить салон
            </Button>
          </form>
        )}
      </Card>
    </>,
  );
}

/** Пока владельца нет: кому ушло приглашение и кнопка отправить его снова (новая ссылка на 7 дней). */
function OwnerInviteCard({
  salonId,
  pending,
  mailFailed,
  onDone,
}: {
  salonId: string;
  pending: { email: string; expiresAt: Date } | null;
  mailFailed: boolean;
  onDone: () => void;
}) {
  const [email, setEmail] = useState(pending?.email ?? "");
  useEffect(() => {
    if (pending?.email) setEmail(pending.email);
  }, [pending?.email]);
  const invite = useMutation(trpc.auth.admin.inviteOwner.mutationOptions({ onSuccess: onDone }));
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    invite.mutate({ salonId, email: email.trim() });
  };
  const sent = invite.data;
  return (
    <Card>
      <h2 className="mb-2 font-semibold">Владелец</h2>
      <p className="mb-3 text-sm text-ink-2">
        {pending
          ? `Приглашение выписано на ${pending.email}, действует до ${fmtDate(pending.expiresAt)}. Владелец войдёт с этой почтой и примет его. Повторная отправка отзывает прежнюю ссылку.`
          : "Приглашения нет или оно просрочено. Отправьте владельцу новое."}
      </p>
      {mailFailed && !sent && (
        <div className="mb-3">
          <Notice kind="error">Салон создан, но письмо владельцу не ушло. Отправьте приглашение снова.</Notice>
        </div>
      )}
      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-2">
        <div>
          <Label htmlFor="owner-email">Почта владельца</Label>
          <Input id="owner-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-64" required />
        </div>
        <Button type="submit" variant="secondary" disabled={invite.isPending || !email.trim()}>
          {pending ? "Отправить снова" : "Отправить приглашение"}
        </Button>
      </form>
      {sent?.mailed === true && (
        <div className="mt-3">
          <Notice kind="success">Письмо отправлено на {sent.invite.email}.</Notice>
        </div>
      )}
      {sent?.mailed === false && (
        <div className="mt-3">
          <Notice kind="error">Приглашение создано, но письмо не ушло. Попробуйте ещё раз.</Notice>
        </div>
      )}
      {invite.isError && (
        <div className="mt-3">
          <Notice kind="error">{invite.error.message}</Notice>
        </div>
      )}
    </Card>
  );
}
